import { cn } from '@/lib/cn'
import { Bone } from '@/components/ui/Skeleton'

export type SectionLoaderVariant = 'table' | 'list' | 'cards' | 'grid' | 'detail'

export interface SectionLoaderProps {
  /** Shape of the content that is about to land. */
  variant?: SectionLoaderVariant
  /** Row / item count for table, list and cards. */
  rows?: number
  /** Column count for table and grid. */
  cols?: number
  /** Screen-reader description, e.g. "Loading leave requests". */
  label?: string
  className?: string
}

// Deterministic width rhythm so placeholder text reads like real, uneven copy.
const WIDTHS = ['w-[72%]', 'w-[48%]', 'w-[86%]', 'w-[60%]', 'w-[38%]', 'w-[66%]', 'w-[54%]']
const w = (i: number): string => WIDTHS[i % WIDTHS.length]

/**
 * Content-shaped loading state for page sections, tables, panels and modals.
 * Mirrors the layout that is about to appear so nothing jumps when data lands,
 * and fades in after a short beat so fast responses never flash a skeleton.
 */
export function SectionLoader({
  variant = 'list',
  rows,
  cols,
  label = 'Loading',
  className,
}: SectionLoaderProps): React.ReactElement {
  return (
    <div role="status" aria-busy="true" aria-label={label} className={cn('kc-loader-in w-full', className)}>
      {variant === 'table' && <TableBones rows={rows ?? 6} cols={cols ?? 5} />}
      {variant === 'list' && <ListBones rows={rows ?? 5} />}
      {variant === 'cards' && <CardBones count={rows ?? 6} />}
      {variant === 'grid' && <GridBones rows={rows ?? 5} cols={cols ?? 7} />}
      {variant === 'detail' && <DetailBones rows={rows ?? 5} />}
      <span className="sr-only">{label}</span>
    </div>
  )
}

SectionLoader.displayName = 'SectionLoader'

function TableBones({ rows, cols }: { rows: number; cols: number }): React.ReactElement {
  return (
    <div className="overflow-hidden">
      <div className="flex gap-6 border-b border-gray-200 px-5 py-3.5 dark:border-gray-700">
        {Array.from({ length: cols }).map((_, c) => (
          <div key={c} className={cn('min-w-0', c === 0 ? 'flex-[1.6]' : 'flex-1')}>
            <Bone className="h-2.5 w-16" />
          </div>
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div
          key={r}
          className="flex items-center gap-6 border-b border-gray-100 px-5 py-4 last:border-b-0 dark:border-gray-700/70"
        >
          {Array.from({ length: cols }).map((_, c) =>
            c === 0 ? (
              <div key={c} className="flex min-w-0 flex-[1.6] items-center gap-3">
                <Bone className="h-7 w-7 shrink-0 rounded-full" tint />
                <Bone className={cn('h-3', w(r))} />
              </div>
            ) : (
              <div key={c} className="min-w-0 flex-1">
                {c === cols - 1 ? (
                  <Bone className="h-5 w-14 rounded-full" tint={r % 3 === 0} />
                ) : (
                  <Bone className={cn('h-3', w(r + c * 2))} />
                )}
              </div>
            )
          )}
        </div>
      ))}
    </div>
  )
}

function ListBones({ rows }: { rows: number }): React.ReactElement {
  return (
    <div className="divide-y divide-gray-100 dark:divide-gray-700/70">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-3 px-1 py-3.5">
          <Bone className="h-9 w-9 shrink-0 rounded-full" tint />
          <div className="min-w-0 flex-1 space-y-2">
            <Bone className={cn('h-3', w(r))} />
            <Bone className={cn('h-2.5', w(r + 3), 'max-w-[40%]')} />
          </div>
          <Bone className="h-6 w-16 shrink-0 rounded-full" />
        </div>
      ))}
    </div>
  )
}

function CardBones({ count }: { count: number }): React.ReactElement {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-[var(--radius-panel)] border border-gray-200 bg-white p-4 shadow-[var(--shadow-panel)] dark:border-gray-700 dark:bg-gray-800"
        >
          <div className="flex items-center gap-3">
            <Bone className="h-8 w-8 shrink-0 rounded-lg" tint />
            <div className="min-w-0 flex-1 space-y-2">
              <Bone className={cn('h-3', w(i))} />
              <Bone className="h-2.5 w-1/3" />
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <Bone className="h-2.5 w-full" />
            <Bone className={cn('h-2.5', w(i + 2))} />
          </div>
        </div>
      ))}
    </div>
  )
}

/** Scheduling-grid shape: a people column and day columns with shift blocks. */
function GridBones({ rows, cols }: { rows: number; cols: number }): React.ReactElement {
  return (
    <div className="overflow-hidden">
      <div
        className="grid border-b border-gray-200 dark:border-gray-700"
        style={{ gridTemplateColumns: `11rem repeat(${cols}, minmax(0, 1fr))` }}
      >
        <div className="px-4 py-3">
          <Bone className="h-2.5 w-14" />
        </div>
        {Array.from({ length: cols }).map((_, c) => (
          <div key={c} className="flex flex-col items-center gap-1.5 border-l border-gray-100 py-2.5 dark:border-gray-700/70">
            <Bone className="h-2 w-7" />
            <Bone className="h-3.5 w-5" tint />
          </div>
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div
          key={r}
          className="grid border-b border-gray-100 last:border-b-0 dark:border-gray-700/70"
          style={{ gridTemplateColumns: `11rem repeat(${cols}, minmax(0, 1fr))` }}
        >
          <div className="flex items-center gap-2.5 px-4 py-3">
            <Bone className="h-7 w-7 shrink-0 rounded-full" tint />
            <Bone className={cn('h-3', w(r))} />
          </div>
          {Array.from({ length: cols }).map((_, c) => (
            <div key={c} className="border-l border-gray-100 p-1.5 dark:border-gray-700/70">
              {(r * 3 + c * 5) % 7 < 4 ? <Bone className="h-full min-h-9 w-full rounded-md" tint={(r + c) % 3 === 0} /> : null}
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

/** Detail panel / modal body: heading block, then label/value pairs. */
function DetailBones({ rows }: { rows: number }): React.ReactElement {
  return (
    <div className="space-y-6 py-2">
      <div className="flex items-center gap-3">
        <Bone className="h-11 w-11 shrink-0 rounded-full" tint />
        <div className="min-w-0 flex-1 space-y-2">
          <Bone className="h-3.5 w-2/5" />
          <Bone className="h-2.5 w-1/4" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
        {Array.from({ length: rows * 2 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Bone className="h-2.5 w-20" />
            <Bone className={cn('h-3', w(i))} />
          </div>
        ))}
      </div>
    </div>
  )
}
